/* import __COLOCATED_TEMPLATE__ from './ai-translation-settings.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type TeammateAiTranslationSettings from 'embercom/services/teammate-ai-translation-settings';
import { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';
interface Args {}

interface Signature {
  Args: Args;
  Element: never;
}

export default class AiTranslationSettings extends Component<Signature> {
  @service declare teammateAiTranslationSettings: TeammateAiTranslationSettings;
  @service declare appService: AppService;
  @service declare session: $TSFixMe;
  @service declare store: $TSFixMe;

  @tracked messengerSettings: any = null;

  constructor(owner: $TSFixMe, args: Args) {
    super(owner, args);
    this.loadMessengerSettings();
  }

  private async loadMessengerSettings() {
    try {
      if (this.app?.id) {
        this.messengerSettings = await this.store.findRecord('messenger-settings/all', this.app.id);
      }
    } catch (error) {
      console.error('Failed to load messenger settings:', error);
    }
  }

  get app() {
    return this.appService.app;
  }

  get languageSettings() {
    return this.messengerSettings?.languages;
  }

  get supportedLocales() {
    return this.languageSettings?.supportedLocales || [];
  }

  get defaultLocaleObject() {
    return this.supportedLocales.find(
      (locale: any) => locale.localeId === this.languageSettings?.defaultLocale,
    );
  }

  get isInboxTranslationEnabled() {
    return this.teammateAiTranslationSettings.isInboxTranslationEnabled;
  }

  get excludedLanguages() {
    return this.teammateAiTranslationSettings.excludedLanguages || [];
  }

  get excludedLanguageObjects() {
    return this.supportedLocales.filter((lang: any) =>
      this.excludedLanguages.includes(lang.localeId),
    );
  }

  get appLocale() {
    return this.session.locale.split('-')[0];
  }

  get availableLanguageOptions() {
    if (!this.supportedLocales || this.supportedLocales.length === 0) {
      return [];
    }

    return this.supportedLocales
      .filter(
        (lang: any) => lang && lang.localeId && !this.excludedLanguages.includes(lang.localeId),
      )
      .map((lang: any) => ({
        value: lang.localeId,
        text: lang.name,
        locale: lang,
      }));
  }

  get isLoading() {
    return (
      taskFor(this.teammateAiTranslationSettings.toggleInboxTranslationConfiguration).isRunning ||
      taskFor(this.teammateAiTranslationSettings.fetchSettings).isRunning ||
      taskFor(this.teammateAiTranslationSettings.updateExcludedLanguages).isRunning
    );
  }

  get isMainToggleLoading() {
    return (
      taskFor(this.teammateAiTranslationSettings.toggleInboxTranslationConfiguration).isRunning ||
      taskFor(this.teammateAiTranslationSettings.fetchSettings).isRunning
    );
  }

  @action
  async toggleInboxTranslationConfiguration() {
    // If we're turning off translation, clear excluded languages first
    if (this.isInboxTranslationEnabled && this.excludedLanguages.length > 0) {
      await taskFor(this.teammateAiTranslationSettings.updateExcludedLanguages).perform([]);
    }

    return taskFor(
      this.teammateAiTranslationSettings.toggleInboxTranslationConfiguration,
    ).perform();
  }

  @action
  addExcludedLanguage(languageOption: any) {
    let newExcludedLanguages = [...this.excludedLanguages, languageOption];
    return taskFor(this.teammateAiTranslationSettings.updateExcludedLanguages).perform(
      newExcludedLanguages,
    );
  }

  @action
  removeExcludedLanguage(localeId: string) {
    let newExcludedLanguages = this.excludedLanguages.filter((code) => code !== localeId);

    return taskFor(this.teammateAiTranslationSettings.updateExcludedLanguages).perform(
      newExcludedLanguages,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Personal::AccountSecurity::AiTranslationSettings': typeof AiTranslationSettings;
  }
}
